import {
  AlertTriangle,
  BarChart3,
  Building2,
  Layers,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  Star,
  Tag,
  User,
  Users,
  Video,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { Button } from '~/components/ui/button';
import { useAuth } from '~/contexts/auth-context';

const navigation = [
  { name: 'Analytics', href: '/', icon: BarChart3 },
  { name: 'Comments', href: '/comments', icon: MessageSquare },
  { name: 'Reports', href: '/reports', icon: AlertTriangle },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Featured Content', href: '/featured', icon: Star },
  { name: 'Settings', href: '/settings', icon: Settings },
  { type: 'divider' as const },
  { name: 'Videos', href: '/videos', icon: Video },
  { name: 'Idols', href: '/idols', icon: User },
  { name: 'Categories', href: '/categories', icon: Tag },
  { name: 'Studios', href: '/studios', icon: Building2 },
  { name: 'Series', href: '/series', icon: Layers },
];

export default function DashboardLayout() {
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Close sidebar on route change (mobile)
    setSidebarOpen(false);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='text-center'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className='flex h-screen bg-background'>
      {/* Sidebar for desktop */}
      <aside className='hidden lg:flex lg:flex-col lg:w-64 lg:border-r lg:border-border'>
        <div className='flex h-16 items-center gap-2 border-b border-border px-6'>
          <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold'>
            JF
          </div>
          <div>
            <h1 className='text-lg font-bold'>JAVFacts</h1>
            <p className='text-xs text-muted-foreground'>Admin Dashboard</p>
          </div>
        </div>

        <nav className='flex-1 space-y-1 px-3 py-4'>
          {navigation.map((item, index) => {
            if ('type' in item && item.type === 'divider') {
              return (
                <div key={`divider-${index}`} className='py-2'>
                  <div className='border-t border-border' />
                  <p className='text-xs font-semibold text-muted-foreground px-3 pt-3 pb-1'>CONTENT</p>
                </div>
              );
            }
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href!}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <item.icon className='h-5 w-5' />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className='border-t border-border p-4'>
          <div className='flex items-center gap-3 mb-3'>
            <div className='h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm'>
              {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium truncate'>{user?.username || user?.email}</p>
              <p className='text-xs text-muted-foreground'>Admin</p>
            </div>
          </div>
          <Button variant='outline' size='sm' onClick={handleLogout} className='w-full'>
            <LogOut className='mr-2 h-4 w-4' />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className='fixed inset-0 z-50 lg:hidden'>
          <div className='fixed inset-0 bg-black/50' onClick={() => setSidebarOpen(false)} />
          <aside className='fixed inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col'>
            <div className='flex h-16 items-center gap-2 border-b border-border px-6'>
              <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold'>
                JF
              </div>
              <div>
                <h1 className='text-lg font-bold'>JAVFacts</h1>
                <p className='text-xs text-muted-foreground'>Admin Dashboard</p>
              </div>
            </div>

            <nav className='flex-1 space-y-1 px-3 py-4'>
              {navigation.map((item, index) => {
                if ('type' in item && item.type === 'divider') {
                  return (
                    <div key={`divider-${index}`} className='py-2'>
                      <div className='border-t border-border' />
                      <p className='text-xs font-semibold text-muted-foreground px-3 pt-3 pb-1'>CONTENT</p>
                    </div>
                  );
                }
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <item.icon className='h-5 w-5' />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className='border-t border-border p-4'>
              <div className='flex items-center gap-3 mb-3'>
                <div className='h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm'>
                  {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium truncate'>{user?.username || user?.email}</p>
                  <p className='text-xs text-muted-foreground'>Admin</p>
                </div>
              </div>
              <Button variant='outline' size='sm' onClick={handleLogout} className='w-full'>
                <LogOut className='mr-2 h-4 w-4' />
                Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className='flex flex-1 flex-col overflow-hidden'>
        {/* Top bar */}
        <header className='flex h-16 items-center gap-4 border-b border-border px-6'>
          <Button variant='ghost' size='icon' className='lg:hidden' onClick={() => setSidebarOpen(true)}>
            <Menu className='h-6 w-6' />
          </Button>
          <div className='flex-1'>
            <h2 className='text-xl font-semibold'>
              {navigation.find((item) => item.href === location.pathname)?.name || 'Dashboard'}
            </h2>
          </div>
        </header>

        {/* Page content */}
        <main className='flex-1 overflow-y-auto p-6'>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
